/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {AugmentedSmarterTrailerSensorData, BrakeChamberStatusType} from '@ion/core/src/types/entities';
import {dateFormatter, timeFormatter} from '@ion/core/src/utils/DateUtil';
import BrakeChamberIcon from '@ion/dls/src/icons/BrakeChamberIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {getTimeDifference} from './helper';
import WidgetContainer from './WidgetContainer';
import WidgetDescription from './WidgetDescription';

export interface BrakeChamberWidgetProps {
  trailerData: AugmentedSmarterTrailerSensorData;
  status: string
}

/**
 * Defines the Brake Chamber Widget component.
 *
 * ```typescript
 *
 * import {BrakeChamberWidget} from '@ion/dls/src/components/smarterTrailer';
 *
 * <BrakeChamberWidget trailerData={trailerData} />
 *
 * ```
 */
const BrakeChamberWidget = ({
  trailerData,
  status
}: BrakeChamberWidgetProps) => {
  const {BMS: {messageTime}} = trailerData || {};
  const messageDate          = dateFormatter(messageTime, UserPreferences.getSystemDateFormat());
  const today                = getTimeDifference(messageTime);
  const timeStamp            = isNil(today) ? timeFormatter(messageTime, UserPreferences.getSystemTimeFormat()) : today;
  const label                = translate('common:smarterTrailer.brakeChamber.widgetLabel');
  const style                = {color: status === BrakeChamberStatusType.Drag ? 'red' : dlsColor('grey2')};
  const value                = translate(`common:smarterTrailer.brakeChamber.${status}`);

  return (
    <WidgetContainer>
      <BrakeChamberIcon />
      <WidgetDescription style={{marginTop: '-2px'}}
        label={label}
        value={value}
        messageTime={timeStamp}
        messageDate={messageDate}
        valueStyle={style} />
    </WidgetContainer>
  );
};

export default BrakeChamberWidget;
